import React, { useEffect, useState } from "react";
import { Gift, TrendUp, ArrowDown, Clock } from "@phosphor-icons/react";
import { PageLoader, Stat, EmptyState } from "../../components/shared";
import api, { money } from "../../lib/api";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { RefreshBar } from "../../components/RefreshBar";

export default function Rewards() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/rewards").then((r) => setData(r.data)).finally(() => setLoading(false));
  const { refreshing, lastUpdated, refresh } = useAutoRefresh(load, []);
  if (loading) return <PageLoader />;

  const worth = money((data.balance || 0) * (data.point_value || 1));

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-6 py-8">
      <RefreshBar refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />
      <div className="overline mb-1">Loyalty</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Event Credits</h1>

      <div className="rounded-2xl bg-foreground text-background p-8 grain relative overflow-hidden mb-6">
        <div className="relative z-[2]">
          <div className="flex items-center gap-2 text-background/60 text-xs uppercase tracking-widest"><Gift weight="fill" className="text-primary" /> Available balance</div>
          <div className="font-heading text-5xl font-bold tracking-tighter mt-2">{data.balance} <span className="text-2xl font-medium text-background/70">pts</span></div>
          <div className="text-background/70 mt-1">≈ {worth} redeemable value</div>
          <div className="mt-4 text-xs text-background/60">
            Earn {data.config?.points_per_100} pts per ₹100 · 1 pt = {money(data.point_value)} · up to {data.config?.max_redeem_percent}% off per booking
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <Stat label="Total Earned" value={`${data.earned} pts`} />
        <Stat label="Total Redeemed" value={`${data.redeemed} pts`} />
      </div>

      <h2 className="font-heading text-xl font-semibold mb-3">History</h2>
      {data.transactions.length === 0 ? (
        <EmptyState icon={Clock} title="No activity yet" subtitle="Earn credits automatically when you book events." testid="no-rewards" />
      ) : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border">
          {data.transactions.map((t) => (
            <div key={t.id} className="flex items-center justify-between p-4" data-testid={`reward-txn-${t.id}`}>
              <div className="flex items-center gap-3">
                <span className={`w-9 h-9 grid place-items-center rounded-full ${t.points >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                  {t.points >= 0 ? <TrendUp size={16} /> : <ArrowDown size={16} />}
                </span>
                <div>
                  <div className="font-medium text-sm">{t.reason}</div>
                  <div className="text-xs text-muted-foreground capitalize">{t.type} · {new Date(t.created_at).toLocaleDateString("en-IN")}</div>
                </div>
              </div>
              <div className={`font-semibold ${t.points >= 0 ? "text-emerald-700" : "text-foreground"}`}>{t.points >= 0 ? "+" : ""}{t.points}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
